//#region IMPORTACIONES
import { useState, useEffect, useRef } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Button,
  Tabs,
  Tab,
  Box,
  Typography,
  Alert,
  AlertTitle,
  Chip,
  Skeleton,
} from "@mui/material";
import {
  AttachMoney as MoneyIcon,
  CreditCard as CreditCardIcon,
  AccountBalance as BankIcon,
  Error as ErrorIcon,
} from "@mui/icons-material";

import {
  getTotalEfectivo,
  getTotalRedondeo,
  getTotalInstrumentos,
  getTotalTarjetas,
  getTotalRetenciones,
  getTotalRecibo,
  getTotalIIBB,
} from "../selectors/receipt.selectors";
import UseReceipt from "../hooks/UseReceipt";
import { useToast } from "../../../../../../../Hooks/UseToast";
import { UrlApi } from "../../../../../../../services/apirest";
import DatosReciboCard from "./DatosRecibo/DatosReciboCard";
import RetencionesCard from "./Retenciones/RetencionesCard";
import { ResumenCard } from "./resumen/ResumenCard";
import EfectivoTab from "./MetodosDePago/EfectivoTab";
import InstrumentosTab from "./MetodosDePago/InstrumentosTab";
import TarjetasTab from "./MetodosDePago/TarjetasTab";
import IIBBModal from "./IIBB/IIBBModal";
import ImputacionModal from "./Imputaciones/ImputacionModal";
import ReciboDetalleContainer from "./MostrarDetalleRecibo/ReciboDetalleContainer";
import AccionesBar from "../../../../../../ventas/reciboContado/AccionesBar";
//#endregion
function TabPanel({ children, value, index }) {
  return (
    <div role="tabpanel" hidden={value !== index}>
      {value === index && <Box sx={{ py: 2 }}>{children}</Box>}{" "}
    </div>
  );
}

export default function ReceiptDialog({
  open,
  onClose,
  clienteData,
  idNegocioCliente = 1,
}) {
  const {
    state,
    submit,
    confirm,
    updateRecibo,
    updateFechaDePago,
    updateNumRecibo,
    updateRetencion,
    addIIBB,
    updateIIBB,
    removeIIBB,
    addInstrumento,
    updateInstrumento,
    removeInstrumento,
    addTarjeta,
    updateTarjeta,
    removeTarjeta,
    setImputaciones,
  } = UseReceipt();

  // console.log("Cliente data: ", clienteData);

  const [tabActivo, setTabActivo] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [erroresValidacion, setErroresValidacion] = useState([]);
  const [isLoadingData, setIsLoadingData] = useState(true);
  const [iibbModalOpen, setIibbModalOpen] = useState(false);

  const [provincias, setProvincias] = useState([]);
  const [tiposInstrumento, setTiposInstrumento] = useState([]);
  const [bancos, setBancos] = useState([]);
  const [tiposTarjeta, setTiposTarjeta] = useState([]);
  const [marcasTarjeta, setMarcasTarjeta] = useState([]);

  const [showDetalleDialog, setShowDetalleDialog] = useState(false);
  const [imputacionModalOpen, setImputacionModalOpen] = useState(false);

  const [previewData, setPreviewData] = useState(null);

  const [valorMaximoRedondeo, setValorMaximoRedondeo] = useState(null);

  const dialogContentRef = useRef(null);
  const erroresRef = useRef(null);

  const { Toast, showToast } = useToast();

  // Inicializar datos del cliente
  useEffect(() => {
    if (open && clienteData) {
      updateRecibo("clienteCuit", clienteData.cuit || "");
      updateRecibo("numCliente", clienteData.nro_cliente || "");
      updateNumRecibo("ptoVta", clienteData.id_suc_sisven?.toString() || "");
      updateRecibo("idTipoNegocio", idNegocioCliente); // thesame👇
      updateRecibo("idCliente", clienteData.id); //El fallback en los dos esta en null para que de error y no te deje cargar si no lo tiene cargado

      const today = new Date();
      updateFechaDePago("dia", today.getDate().toString().padStart(2, "0"));
      updateFechaDePago(
        "mes",
        (today.getMonth() + 1).toString().padStart(2, "0"),
      );
      updateFechaDePago("ano", today.getFullYear().toString());
    }
  }, [open, clienteData]);

  // Cargar datos de catálogos
  useEffect(() => {
    const fetchData = async () => {
      setIsLoadingData(true);
      try {
        const [
          resProvincias,
          resBancos,
          resMediosDePago,
          resMarcasTarjeta,
          redondeoMaximo,
        ] = await Promise.all([
          fetch(`${UrlApi}/provincias`).then((r) => r.json()),
          fetch(`${UrlApi}/bancos`).then((r) => r.json()),
          fetch(`${UrlApi}/ctacte/recibos/tipos/pago`).then((r) => r.json()),
          fetch(`${UrlApi}/ctacte/recibos/marcas/tarjeta`).then((r) => r.json()),
          fetch(`${UrlApi}/ctacte/recibos/maximos/redondeo`).then((r) =>
            r.json(),
          ),
        ]);

        setProvincias(resProvincias);
        setBancos(resBancos);
        setMarcasTarjeta(
          (Array.isArray(resMarcasTarjeta) ? resMarcasTarjeta : []).filter(
            (m) => m.anulada === 0,
          ),
        );
        setTiposTarjeta(
          resMediosDePago.filter((p) => p.tarjeta === 1 && p.anulada === 0),
        );
        setTiposInstrumento(
          resMediosDePago.filter((p) => p.tarjeta === 0 && p.anulada === 0),
        );
        setValorMaximoRedondeo(redondeoMaximo);
      } catch (error) {
        console.error("Error al cargar datos:", error);
      } finally {
        setIsLoadingData(false);
      }
    };

    if (open) {
      fetchData();
    }
  }, [open]);

  // Scroll a errores
  useEffect(() => {
    if (erroresValidacion.length > 0) {
      setTimeout(() => {
        if (erroresRef.current) {
          erroresRef.current.scrollIntoView({
            behavior: "smooth",
            block: "start",
          });
        }
      }, 100);
    }
  }, [erroresValidacion]);

  // Validar campos de importes
  const handleImporteChange = (e, updateFn) => {
    const value = e.target.value;
    if (value.startsWith(",") || value.startsWith(".")) return;
    if (value !== "" && !/^\d*\.?\d*$/.test(value)) return;
    if (value !== "" && parseFloat(value) < 0) return;
    updateFn(value);
  };

  // Calcular totales
  const totalEfectivo = getTotalEfectivo(state);
  const totalRedondeo = getTotalRedondeo(state);
  const totalInstrumentos = getTotalInstrumentos(state);
  const totalTarjetas = getTotalTarjetas(state);
  const totalRetenciones = getTotalRetenciones(state);
  const totalIIBB = getTotalIIBB(state);
  const totalCobrado = getTotalRecibo(state);

  const totalEsperado = parseFloat(state.recibo.totalEsperado) || 0;
  const totalesCoinciden =
    totalEsperado > 0 && Math.abs(totalCobrado - totalEsperado) < 0.01;
  const diferencia = totalCobrado - totalEsperado;

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("es-AR", {
      style: "currency",
      currency: "ARS",
    }).format(amount);
  };

  const getProvinciaNombre = (id) => {
    return provincias.find((p) => p.id === id)?.provincia || "";
  };

  const handlePreview = () => {
    setErroresValidacion([]);

    const result = submit();

    if (!result.ok) {
      setErroresValidacion(result.errors);
      showToast("Corrige los errores del formulario antes de guardar", "error");
      return;
    }

    // console.log("RESULT: ", result);
    setPreviewData(result.payload);
  };

  useEffect(() => {
    if (previewData) {
      setShowDetalleDialog(true);
    }
  }, [previewData]);

  const handleConfirm = async () => {
    setIsSubmitting(true);
    try {
      await confirm(previewData, {
        valorMaximoRedondeo,
        onSuccess: (msg) => {
          showToast(msg, "success");
          setShowDetalleDialog(false);
          onClose();
        },
        onWarning: (msg) => {
          showToast(msg, "warning");
          setShowDetalleDialog(false);
          onClose();
        },
        onError: (msg) => showToast(msg, "error"),
      });
    } catch (error) {
      showToast(
        error.message.length !== 0 ? error.message : error.error,
        "error",
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <Dialog open={open} onClose={onClose} fullScreen>
        <DialogTitle>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
          >
            <Typography variant="h5" fontWeight="bold">
              Cargar Recibo - {clienteData?.razon_social}
            </Typography>
            <Chip
              label={`CUIT: ${state.recibo.clienteCuit}`}
              color="primary"
              variant="outlined"
            />
          </Box>
        </DialogTitle>

        <DialogContent dividers ref={dialogContentRef}>
          {isLoadingData && (
            <Box>
              <Skeleton variant="rectangular" height={200} sx={{ mb: 2 }} />
              <Skeleton variant="rectangular" height={300} />
            </Box>
          )}

          {!isLoadingData && (
            <>
              {erroresValidacion.length > 0 && (
                <Alert
                  severity="error"
                  sx={{ mb: 2 }}
                  icon={<ErrorIcon />}
                  ref={erroresRef}
                >
                  <AlertTitle>Errores de validación</AlertTitle>
                  <ul style={{ margin: 0, paddingLeft: 20 }}>
                    {erroresValidacion.map((error, i) => (
                      <li key={i}>{error}</li>
                    ))}
                  </ul>
                </Alert>
              )}

              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "300px 1fr 400px", // Datos | Retenciones | Resumen
                  gap: 2,
                  mb: 2,
                }}
              >
                {/* COLUMNA 1: Datos del recibo */}
                <DatosReciboCard
                  totalEsperado={state.recibo.totalEsperado}
                  onTotalEsperadoChange={(value) =>
                    updateRecibo("totalEsperado", value)
                  }
                  FechaDePago={state.recibo.FechaDePago}
                  onFechaChange={updateFechaDePago}
                  numRecibo={state.recibo.numRecibo}
                  onNumReciboChange={updateNumRecibo}
                  usaReciboElectronico={state.recibo.usaReciboElectronico}
                  onReciboElectronicoChange={(value) =>
                    updateRecibo("usaReciboElectronico", value)
                  }
                  handleImporteChange={handleImporteChange}
                />

                {/* COLUMNA 2: Retenciones */}
                <RetencionesCard
                  retSuss={state.retenciones.retSuss}
                  retIva={state.retenciones.retIva}
                  retGanancias={state.retenciones.retGanancias}
                  retDrei={state.retenciones.retDrei}
                  retencionesIIBB={state.retenciones.retencionesIIBB}
                  totalIIBB={totalIIBB}
                  onRetencionChange={updateRetencion}
                  onOpenIIBBModal={() => setIibbModalOpen(true)}
                  handleImporteChange={handleImporteChange}
                  formatCurrency={formatCurrency}
                  getProvinciaNombre={getProvinciaNombre}
                />

                {/* COLUMNA 3: Resumen */}
                <ResumenCard
                  totalRedondeo={totalRedondeo}
                  totalEfectivo={totalEfectivo}
                  totalInstrumentos={totalInstrumentos}
                  totalTarjetas={totalTarjetas}
                  totalRetenciones={totalRetenciones}
                  totalGeneral={totalCobrado}
                  totalesCoinciden={totalesCoinciden}
                  diferencia={diferencia}
                  totalEsperado={totalEsperado}
                  instrumentos={state.instrumentos}
                  tarjetas={state.tarjetas}
                  formatCurrency={formatCurrency}
                />
              </Box>

              {/* Tabs de métodos de pago */}
              <Box>
                <Tabs
                  value={tabActivo}
                  onChange={(e, newValue) => setTabActivo(newValue)}
                  variant="fullWidth"
                  sx={{
                    borderBottom: 1,
                    borderColor: "divider",
                    minHeight: 36,
                    "& .MuiTab-root": {
                      minHeight: 36,
                      fontSize: "0.8rem",
                      padding: "6px 10px",
                    },
                  }}
                >
                  <Tab
                    icon={<MoneyIcon />}
                    label="Efectivo"
                    iconPosition="start"
                  />
                  <Tab
                    icon={<BankIcon />}
                    label={
                      <Box display="flex" alignItems="center" gap={1}>
                        Instrumentos
                        {state.instrumentos.length > 0 && (
                          <Chip
                            label={state.instrumentos.length}
                            size="small"
                            color="primary"
                          />
                        )}
                      </Box>
                    }
                    iconPosition="start"
                  />
                  <Tab
                    icon={<CreditCardIcon />}
                    label={
                      <Box display="flex" alignItems="center" gap={1}>
                        Tarjetas
                        {state.tarjetas.length > 0 && (
                          <Chip
                            label={state.tarjetas.length}
                            size="small"
                            color="primary"
                          />
                        )}
                      </Box>
                    }
                    iconPosition="start"
                  />
                </Tabs>

                <TabPanel value={tabActivo} index={0}>
                  <EfectivoTab
                    efectivo={state.recibo.efectivo}
                    onEfectivoChange={(value) =>
                      updateRecibo("efectivo", value)
                    }
                    redondeo={state.recibo.redondeo}
                    onRedondeoChange={(value) =>
                      updateRecibo("redondeo", value)
                    }
                    handleImporteChange={handleImporteChange}
                  />
                </TabPanel>

                <TabPanel value={tabActivo} index={1}>
                  <InstrumentosTab
                    instrumentos={state.instrumentos}
                    clienteData={clienteData}
                    onAdd={addInstrumento}
                    onUpdate={updateInstrumento}
                    onRemove={removeInstrumento}
                    tiposInstrumento={tiposInstrumento}
                    bancos={bancos}
                    handleImporteChange={handleImporteChange}
                  />
                </TabPanel>

                <TabPanel value={tabActivo} index={2}>
                  <TarjetasTab
                    tarjetas={state.tarjetas}
                    onAdd={addTarjeta}
                    onUpdate={updateTarjeta}
                    onRemove={removeTarjeta}
                    tiposTarjeta={tiposTarjeta}
                    bancos={bancos}
                    marcas={marcasTarjeta}
                    handleImporteChange={handleImporteChange}
                  />
                </TabPanel>
              </Box>
            </>
          )}
          <AccionesBar
            state={state}
            handlePreview={handlePreview}
            totalesCoinciden={totalesCoinciden}
            totalCobrado={totalCobrado}
            isSubmitting={isSubmitting}
            recObligatorio={false}
            setImputacionModalOpen={setImputacionModalOpen}
            aplicable={true}
            onClose={onClose}
          />
        </DialogContent>
      </Dialog>

      {/* Modal de IIBB */}
      <IIBBModal
        open={iibbModalOpen}
        onClose={() => {
          showToast("Se guardaron las retenciones");
          setIibbModalOpen(false);
        }}
        provincias={provincias}
        setProvincias={setProvincias}
        retenciones={state.retenciones.retencionesIIBB}
        onAdd={addIIBB}
        onUpdate={updateIIBB}
        onDelete={removeIIBB}
        total={totalIIBB}
        formatCurrency={formatCurrency}
        handleImporteChange={handleImporteChange}
      />

      <ReciboDetalleContainer
        open={showDetalleDialog}
        onClose={() => setShowDetalleDialog(false)}
        data={previewData}
        onConfirm={handleConfirm}
        clienteData={clienteData}
      />
      <ImputacionModal
        open={imputacionModalOpen}
        onClose={() => setImputacionModalOpen(false)}
        mode="inline"
        clienteData={clienteData}
        cuit={clienteData?.cuit}
        saldoDisponible={totalCobrado}
        imputaciones={state.imputaciones}
        onSetImputaciones={setImputaciones}
      />
      {Toast}
    </>
  );
}
